// PATH: src/components/catalog/useCatalogDelete.tsx
import * as React from "react";
import { Alert, Box, Typography } from "@mui/material";
import { useDocumentsApi } from "../../api";
import { useConfirm, useToast } from "../ui";
import { plural, writeErrorMessage, type EntityKind } from "./catalogModel";
import { useCatalog } from "./CatalogContext";

/** Filtres de `GET /v1/documents` : le programme est obligatoire côté serveur. */
export interface ChapterScope {
  program_id: string;
  stage_id?: string;
  grade_id?: string;
  subject_id?: string;
}

export interface DeleteRequest {
  entity: EntityKind;
  /** « le niveau L1 — 1re année du bac » */
  label: string;
  /** Ce qui part avec (cascade de la base). */
  cascade?: string[];
  /** Avertissement en plus (ex. offres qui deviennent ouvertes à toutes les filières). */
  warning?: string;
  /** Chapitres importés à supprimer d'abord (voir plus bas). */
  chapters?: ChapterScope;
  /** Mot à retaper : pour les suppressions lourdes. */
  typeToConfirm?: string;
  remove: () => Promise<unknown>;
  /** Appelé après succès (ex. revenir au parent). */
  onDone?: () => void;
}

const PAGE = 200;
/**
 * Suppression confirmée. Les chapitres importés ne partent pas en cascade
 * dans la base : `documents.program_id` est en RESTRICT (le programme refuse
 * d'être supprimé), `stage/grade/subject_id` en SET NULL (le chapitre
 * deviendrait orphelin, invisible) et `track_id` en RESTRICT (un niveau qui a
 * des chapitres de filière refuse aussi). On les compte pour le dire, puis on
 * les supprime avant l'élément — comme le faisaient les anciens écrans.
 */
export const useCatalogDelete = () => {
  const confirm = useConfirm();
  const toast = useToast();
  const { reload } = useCatalog();
  const { listDocuments, deleteDocument } = useDocumentsApi();
  const apiRef = React.useRef({ listDocuments, deleteDocument });
  apiRef.current = { listDocuments, deleteDocument };

  return React.useCallback(
    async (req: DeleteRequest) => {
      let chapterCount: number | null = 0;
      if (req.chapters) {
        try {
          const { data } = await apiRef.current.listDocuments({ ...req.chapters, limit: 1000 });
          chapterCount = data.length;
        } catch {
          chapterCount = null;
        }
      }

      const lines = [...(req.cascade ?? [])];
      if (chapterCount === null) lines.push("les chapitres importés qui y sont rattachés (nombre inconnu)");
      else if (chapterCount > 0)
        lines.push(
          `${plural(chapterCount, "chapitre importé", "chapitres importés")}${chapterCount >= 1000 ? " ou plus" : ""}, avec leurs versions, objectifs et faits`,
        );
      const heavy = Boolean(req.typeToConfirm) && (lines.length > 1 || (chapterCount ?? 1) > 0);

      const ok = await confirm({
        title: "Supprimer définitivement ?",
        danger: true,
        confirmLabel: "Supprimer",
        typeToConfirm: heavy ? req.typeToConfirm : undefined,
        message: (
          <Box>
            <Typography variant="body1" color="text.secondary">
              Vous allez supprimer {req.label}.
              {lines.length > 0 ? " Seront aussi supprimés :" : ""}
            </Typography>
            {lines.length > 0 && (
              <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5, color: "text.secondary", "& li": { fontSize: "0.8125rem", py: 0.25 } }}>
                {lines.map((l) => (
                  <li key={l}>{l}</li>
                ))}
              </Box>
            )}
            {req.warning && (
              <Alert severity="warning" sx={{ mt: 1.5 }}>
                {req.warning}
              </Alert>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              Cette action est irréversible.
            </Typography>
          </Box>
        ),
      });
      if (!ok) return false;

      try {
        let deleted = 0;
        if (req.chapters && chapterCount !== 0) {
          // On relit la 1re page jusqu'à épuisement : chaque tour supprime ce qu'il lit.
          for (let round = 0; round < 100; round++) {
            const { data } = await apiRef.current.listDocuments({ ...req.chapters, limit: PAGE });
            const docs = data;
            if (docs.length === 0) break;
            for (const d of docs) {
              await apiRef.current.deleteDocument(d.id);
              deleted++;
            }
            if (docs.length < PAGE) break;
          }
        }
        await req.remove();
        toast.success(deleted > 0 ? `Supprimé, avec ${plural(deleted, "chapitre")}.` : "Supprimé.");
        req.onDone?.();
        await reload();
        return true;
      } catch (e) {
        toast.error(writeErrorMessage(e, req.entity));
        await reload();
        return false;
      }
    },
    [confirm, toast, reload],
  );
};
